import React from "react";
import { Mail, Share2, QrCode, UserPlus } from 'lucide-react';
import { canonicalCardUrl } from '@/utils/url';
import { useShare } from '@/context/ShareProvider';
import type { TemplateMeta } from "@/api/templates";
import { DEFAULT_COVER } from "@/constants/media";
import { REMOTE_FALLBACK_COVER_URL } from "@/constants/images";
import { DEFAULT_AVATAR, avatarFallbackUrl } from "@/utils/media";
import AdsCarousel from "../common/AdsCarousel";
import { EnhancedEmailLeadForm } from "@/components/EnhancedEmailLeadForm";
import { QRCodeModal } from "@/components/QRCodeModal";
import { BroadcastModal } from "@/components/BroadcastModal";
import BroadcastLoginModal from "@/components/BroadcastLoginModal";
import { resolveMediaUrl } from "@/utils/apiBase";
import safeToast from "@/utils/safeToast";
import { listLeadsByOwner } from "@/api/leads";
import UnifiedBrandIcon from "../../UnifiedBrandIcon";
import { buildSharePayload } from '@/utils/share';

type Props = { template: TemplateMeta; user: any; accent?: string; mode?: "grid" | "full"; allowInnerScroll?: boolean; ads?: any[]; isOwnerView?: boolean; leads?: any[]; onLeadSubmit?: (lead: any) => void; onAnalyticsUpdate?: (...args: any[]) => void; onContactExchange?: () => void };

// Extend the dynamic spec shape to support blocks (v2)
type DynBlocksSpec = {
  header?: {
    type?: string;
    src?: string;
    coverUrl?: string;
    bandColor?: string;
    objectPosition?: string;
    height?: number;
    radius?: number;
  gradient?: string;
  color?: string;
    overlay?: { from?: string; to?: string };
    avatar?: {
      size?: number;
      overlap?: boolean;
      offsetY?: number;
      border?: { color?: string; width?: number };
      shadow?: boolean;
    };
  actions?: { buttons?: Array<{ key?: string; label?: string; bg?: string; fg?: string; square?: number; icon?: string; iconSvg?: string }> };
  };
  hero?: { type?: string; src?: string; height?: number };
  background?: { type?: string; src?: string };
  grid?: { columns?: number; gap?: number };
  blocks?: any[];
  sectionCard?: { radius?: number; border?: { color?: string; width?: number }; padding?: number };
  theme?: {
    bg?: string; cardBg?: string; text?: string; muted?: string; accent?: string;
    buttonBg?: string; buttonFg?: string; saveBg?: string; saveFg?: string; border?: string;
  };
  content?: { title?: string; subtitle?: string; about?: string; saveLabel?: string; adsTitle?: string; adsSubtitle?: string };
  accent?: string;
  // legacy dynamic fields used by v1 renderer
  layout?: string;
  colors?: any;
  palette?: any;
  avatar?: any; logo?: any; profile?: any;
  actions?: any[];
  badge?: { label?: string } | string; tag?: string;
  fallbackName?: string; fallbackTitle?: string; subtitle?: string; company?: string;
  about?: string | { text?: string; content?: string };
  sections?: { about?: { text?: string } };
  copy?: { about?: string };
  bio?: string;
  contact?: { phone?: string; email?: string; website?: string; location?: string };
  phone?: string; email?: string; website?: string; location?: string;
  social?: Record<string, string>;
  saveContact?: { href?: string; label?: string };
};

const BlocksSpecRenderer: React.FC<{ spec: DynBlocksSpec; user: any; mode?: 'grid' | 'full'; ads?: any[]; onAnalyticsUpdate?: (...args: any[]) => void; onLeadSubmit?: (lead: any) => void; accent?: string; isOwnerView?: boolean; onContactExchange?: () => void }>
 = ({ spec, user, mode = 'full', ads = [], onAnalyticsUpdate, onLeadSubmit, accent, isOwnerView, onContactExchange }) => {
  // Resolve theme and content – user colors override template spec
  const userColors = ((user as any)?.colors || {}) as Record<string, string>;
  const theme = {
    bg: userColors.background ?? spec?.theme?.bg ?? '#FFFFFF',
    cardBg: (userColors as any).cardBg ?? spec?.theme?.cardBg ?? '#FFFFFF',
    text: userColors.text ?? spec?.theme?.text ?? '#0F172A',
    muted: userColors.secondary ?? spec?.theme?.muted ?? '#475569',
    accent: userColors.accent ?? userColors.primary ?? accent ?? spec?.theme?.accent ?? (spec as any)?.accent ?? '#123B8A',
    buttonBg: userColors.buttonBg ?? spec?.theme?.buttonBg ?? '#F1F5F9',
    buttonFg: userColors.buttonFg ?? spec?.theme?.buttonFg ?? '#0F1F33',
    saveBg: userColors.saveButton ?? spec?.theme?.saveBg ?? '#FF7C67',
    saveFg: userColors.saveFg ?? spec?.theme?.saveFg ?? '#FFFFFF',
    border: (userColors as any).border ?? spec?.theme?.border ?? '#E6EDF5',
  } as const;
  const content = (spec as any)?.content || {};

  const explicitCover = (spec?.header?.coverUrl as (string | undefined));
  // Prefer user's cover; otherwise always use the system default cover (do not fall back to template art in public or owner views)
  const userCover = (user as any)?.coverUrl as (string | undefined);
  const resolvedCover = (userCover && String(userCover).trim()) || DEFAULT_COVER;
  const headerType = resolvedCover ? 'cover' : ((spec?.header?.type as string) || 'band');
  const objectPosition = (spec?.header?.objectPosition as string) || 'center 15%';
  const overlay = (spec as any)?.header?.overlay as any; // { from, to }
  const bandColor = spec?.header?.bandColor || theme.accent;
  const height = Number(spec?.header?.height ?? 148);
  const radius = Number(spec?.header?.radius ?? 20);

  const av = spec?.header?.avatar || {};
  const avSize = Number(av.size ?? 112);
  const avOverlap = !!av.overlap;
  const avOffsetY = Number(av.offsetY ?? 0);
  const avRing = (av.border && typeof av.border === 'object') ? av.border : {};
  const avRingColor = (avRing as any).color || '#FFFFFF';
  const avRingWidth = Number((avRing as any).width ?? 0);
  const avShadow = av.shadow ?? true;
  const actions = (spec?.header?.actions?.buttons || []) as Array<any>;
  const actionHeight = Number(((spec?.header as any)?.actions?.height ?? 36));

  const gridCols = Number(spec?.grid?.columns ?? 2);
  const gridGap  = spec?.grid?.gap ?? 16;
  const blocks   = Array.isArray(spec?.blocks) ? spec.blocks : [];
  const cardRad  = Number(spec?.sectionCard?.radius ?? 14);
  const cardBd   = spec?.sectionCard?.border || { color: theme.border, width: 1 } as { color: string; width: number };
  const cardPad  = Number(spec?.sectionCard?.padding ?? 18);
  const ac       = String(userColors.accent || userColors.primary || accent || (spec as any)?.accent || theme.accent || '#0F1F33');
  const stripe   = String((userColors as any).stripLine || (userColors as any).stripe || ac);
  const colors   = {
    pageBg: theme.bg,
    cardBg: theme.cardBg,
    text: theme.text,
    muted: theme.muted,
    buttonBg: theme.buttonBg,
    buttonFg: theme.buttonFg,
    saveBg: theme.saveBg,
    saveFg: theme.saveFg,
    border: theme.border,
  } as const;

  // Local UI state for modals used by header actions
  const [showEmail, setShowEmail] = React.useState(false);
  const [showQR, setShowQR] = React.useState(false);
  const { openShare } = useShare();

  // Scale-to-fit for grid previews: scale the entire inner content to avoid scroll/cropping
  const outerRef = React.useRef<HTMLDivElement | null>(null);
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = React.useState<number>(1);
  React.useLayoutEffect(() => {
    if (mode !== 'grid') { setScale(1); return; }
    const el = outerRef.current;
    const content = innerRef.current;
    if (!el || !content) return;
    const compute = () => {
      const avail = el.clientHeight;
      const need = content.scrollHeight;
      const s = need > 0 ? Math.min(1, avail / need) : 1;
      setScale(s);
    };
    compute();
    const RO: any = (window as any).ResizeObserver;
    const ro = RO ? new RO(compute) : null;
    if (ro) {
      try { ro.observe(el); } catch {}
      try { ro.observe(content); } catch {}
    } else {
      window.addEventListener('resize', compute);
    }
    return () => {
      if (ro) {
        try { ro.disconnect(); } catch {}
      } else {
        window.removeEventListener('resize', compute);
      }
    };
  }, [mode]);

  // Helpers
  const cardOwnerId = String(user?.id || (user as any)?._id || (user as any)?.username || '');
  const saveToContacts = () => {
    try {
      const name = (user?.name || user?.displayName || content.title || '');
      const org = (user?.company || '');
      const title = (user?.position || '');
      const phone = (user?.phone || '');
      const email = (user?.email || '');
      const website = (user?.website || '');
      const location = (user as any)?.location || '';
      const socials = ((user as any)?.socialLinks || (user as any)?.social || {}) as Record<string, string>;
      const streaming = ((user as any)?.streamingLinks || {}) as Record<string, string>;
      const lines = [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `N:;${name};;;`,
        `FN:${name}`,
        org ? `ORG:${org}` : '',
        title ? `TITLE:${title}` : '',
        phone ? `TEL;TYPE=CELL:${phone}` : '',
        email ? `EMAIL:${email}` : '',
        website ? `URL:${website}` : '',
        // Address (best-effort; label with unstructured location)
        location ? 'ADR;TYPE=WORK:;;;;;;' : '',
        location ? `LABEL;TYPE=WORK:${location}` : '',
        // Known social profiles (Apple/Google accept X-SOCIALPROFILE widely)
        ...Object.entries(socials).map(([k, v]) => {
          const p = String(k).toLowerCase();
          const url = String(v).trim();
          if (!url) return '';
          if (['twitter','x','facebook','instagram','linkedin','tiktok','youtube'].includes(p)) {
            const type = p === 'x' ? 'twitter' : p;
            return `X-SOCIALPROFILE;type=${type}:${url}`;
          }
          // fallback as an extra URL entry
          return `URL:${url}`;
        }),
        // Streaming links: add as URLs and a grouped NOTE for readability
        ...Object.entries(streaming).map(([platform, url]) => (url ? `URL:${String(url)}` : '')),
        (() => {
          const socialsNote = Object.entries(socials).map(([k,v]) => `${k}: ${v}`).join(' | ');
          const streamingNote = Object.entries(streaming).map(([k,v]) => `${k}: ${v}`).join(' | ');
          const parts = [] as string[];
          if (socialsNote) parts.push(`Socials -> ${socialsNote}`);
          if (streamingNote) parts.push(`Streaming -> ${streamingNote}`);
          return parts.length ? `NOTE:${parts.join(' \n ')}` : '';
        })(),
        'END:VCARD'
      ].filter(Boolean);
      const vCardData = lines.join('\n');
      const blob = new Blob([vCardData], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(name || 'contact').replace(/\s+/g, '_')}.vcf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      try { onAnalyticsUpdate?.(cardOwnerId, 'saves'); } catch {}
    } catch {
      try { safeToast('error', 'Failed to generate contact card'); } catch {}
    }
  };

  // Fill the phone wrapper by default; allow spec.theme.phoneWidth to override (number or string)
  const phoneMaxWidth = (spec as any)?.theme?.phoneWidth ?? '100%';
  return (
    <div ref={outerRef} className="h-full w-full flex" style={{ background: colors.pageBg, color: colors.text, maxWidth: phoneMaxWidth as any }}>
      <div ref={innerRef} className="w-full flex flex-col" style={{ transform: mode === 'grid' ? `scale(${scale})` : undefined, transformOrigin: 'top center', marginTop: mode === 'grid' ? 8 : 0 }}>
      {/* Header */}
      <div className="relative w-full" style={{ height }}>
        {headerType === 'cover' ? (
          <>
            <img alt="cover" src={resolvedCover}
                 className="absolute inset-0 w-full h-full object-cover"
                 style={{ borderTopLeftRadius: radius, borderTopRightRadius: radius, objectPosition }}
                 onError={(e) => {
                   const el = e.currentTarget as HTMLImageElement;
                   // Try local default first, then remote fallback
                   if (el.src !== DEFAULT_COVER) { el.src = DEFAULT_COVER; return; }
                   el.src = REMOTE_FALLBACK_COVER_URL;
                 }} />
            {overlay && (
              <div className="absolute inset-0 pointer-events-none"
                   style={{ borderTopLeftRadius: radius, borderTopRightRadius: radius,
                            background: `linear-gradient(to bottom, ${overlay.from || 'transparent'}, ${overlay.to || 'rgba(255,255,255,.18)'})` }} />
            )}
          </>
        ) : (
          <div className="absolute inset-0" style={{ backgroundColor: bandColor,
                 borderTopLeftRadius: radius, borderTopRightRadius: radius }} />
        )}
      </div>

  {/* Accent stripe below header to reflect user stripLine/stripe color */}
  <div style={{ height: 3, background: stripe }} />

  {/* Avatar */}
      {avOverlap && (
        <div className="flex justify-center" style={{ marginTop: (-(avSize/2) + avOffsetY + (mode === 'grid' ? 10 : 0)), zIndex: 5 }}>
          <div className="rounded-full overflow-hidden bg-white"
               style={{ width: avSize, height: avSize,
                        border: avRingWidth ? `${avRingWidth}px solid ${avRingColor}` : undefined,
                        boxShadow: avShadow ? '0 6px 16px rgba(0,0,0,.12)' : undefined }}>
            {user?.avatarUrl
              ? <img alt="avatar" src={user.avatarUrl} className="w-full h-full object-cover" />
              : <div className="h-full w-full flex items-center justify-center text-slate-600 text-lg font-bold">
                  {(user?.displayName || user?.name || 'U').slice(0,1).toUpperCase()}
                </div>}
          </div>
        </div>
      )}

      {/* Titles */}
      <div className="px-4 text-center mt-2">
        <div className="text-[28px] font-black" style={{ color: colors.text }}>{content.title ?? user?.displayName ?? user?.name ?? 'Preview User'}</div>
        <div className="text-[16px] font-semibold" style={{ color: colors.muted }}>{content.subtitle ?? user?.position ?? "Artist"}</div>
      </div>

      {/* Action buttons */}
      {actions.length > 0 && (
        <div className="flex items-center justify-center gap-3 mt-3 px-4">
          {actions.map((b:any,i:number) => (
      <button key={i} className="rounded-xl text-sm px-4 py-2"
        style={{ background: b?.bg || colors.buttonBg || `${ac}14`, color: b?.fg || colors.buttonFg || ac, border: `1px solid ${colors.border}`,
                          height: actionHeight, minHeight: actionHeight,
                          minWidth: (b?.square || (!b?.label && (String(b?.key||'').toLowerCase()==='qr' || String(b?.key||'').toLowerCase()==='qrcode'))) ? actionHeight : undefined,
                          display:'flex', alignItems:'center', justifyContent:'center' }}
                 onClick={() => {
                   const key = String(b?.key || '').toLowerCase();
                   const title = content.title ?? user?.displayName ?? user?.name ?? 'My Digital Card';
                   if (key === 'email') { setShowEmail(true); return; }
                   if (key === 'share') {
                    try {
                      const handle = (user as any)?.handle || (user as any)?.username || '';
                      const username = (user as any)?.username || (user as any)?.id || '';
                      const fallbackUrl = typeof window !== 'undefined' ? window.location.href : '';
                      const url = (handle && username) ? canonicalCardUrl(handle, username) : (user?.website || fallbackUrl);
                      const payload = buildSharePayload({ name: (user as any)?.name || title, url });
                      const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS/i.test(navigator.userAgent||'');
                      if ((navigator as any)?.share && isMobile) {
                        (navigator as any).share(payload).catch(() => openShare(url, payload.text));
                      } else {
                        openShare(url, payload.text);
                      }
                    } catch { openShare(typeof window !== 'undefined' ? window.location.href : ''); }
                     try { onAnalyticsUpdate?.(cardOwnerId, 'shares'); } catch {}
                     return;
                   }
                   if (key === 'qr' || key === 'qrcode') { setShowQR(true); try { onAnalyticsUpdate?.(cardOwnerId, 'qrScans'); } catch {} return; }
                   if (key === 'save') { saveToContacts(); return; }
                 }}
            >
              {(() => {
                const key = String(b?.key || '').toLowerCase();
                const iconName = String(b?.icon || '').toLowerCase();
                const wantIcon = !!b?.icon || !!b?.iconSvg || (!b?.label && (key === 'qr' || key === 'qrcode' || key === 'email' || key === 'share'));
                const IconComp = (iconName === 'mail' || iconName === 'email' || key === 'email') ? Mail
                  : (iconName === 'share' || key === 'share') ? Share2
                  : (iconName === 'qr' || iconName === 'qrcode' || key === 'qr' || key === 'qrcode') ? QrCode
                  : null;
                return (
                  <span className="inline-flex items-center gap-2" style={{ color: b?.fg || colors.buttonFg }}>
                    {wantIcon && IconComp ? <IconComp className="w-4 h-4" aria-hidden /> : null}
                    {wantIcon && !IconComp && b?.iconSvg ? (
                      <span className="w-4 h-4 inline-block" aria-hidden dangerouslySetInnerHTML={{ __html: b.iconSvg }} />
                    ) : null}
                    {b?.label ?? ''}
                  </span>
                );
              })()}
            </button>
          ))}
        </div>
      )}

      {/* Grid blocks */}
      <div className="px-4 mt-4 grid"
           style={{ gridTemplateColumns:`repeat(${gridCols}, minmax(0,1fr))`, gap:(gridGap as number) }}>
        {blocks.filter((b:any)=>['about','contact','social','streaming'].includes(String(b?.type))).map((b:any,i:number)=>(
          <div key={i}
               className="bg-white"
               style={{ background: colors.cardBg, borderRadius:cardRad, border:`${(cardBd as any).width}px solid ${colors.border}`,
                        padding:cardPad, gridColumn:`span ${Number(b?.span||1)}` }}>
            <div className="font-semibold mb-2" style={{ color: ac }}>{b?.title||b?.type}</div>
            {b?.type==='about' && <div className="text-[13px] leading-snug" style={{ color: colors.muted }}>{(content.about ?? user?.bio) || 'About section…'}</div>}
            {b?.type==='contact' && <div className="text-[13px] space-y-1" style={{ color: colors.text }}>
              <div className="truncate">{user?.phone ? (
                <a
                  href={`tel:${user.phone}`}
                  style={{ color: ac }}
                  onClick={() => { try { onAnalyticsUpdate?.(cardOwnerId, 'callClicks'); } catch {} }}
                >{user.phone}</a>
              ) : '+123-456-7890'}</div>
              <div className="truncate">{user?.email ? (
                <a href="#email" onClick={(e)=>{ e.preventDefault(); setShowEmail(true); }} style={{ color: ac }}>{user.email}</a>
              ) : 'artist@example.com'}</div>
              <div className="break-all">{user?.website ? (<a href={String(user.website)} target="_blank" rel="noopener noreferrer" style={{ color: ac }}>{String(user.website)}</a>) : 'www.example.com'}</div>
            </div>}
            {b?.type==='social' && (() => {
              const PRIORITY = ['facebook','instagram','tiktok','twitter','linkedin'];
              const normalize = (s: string) => String(s || '').trim().toLowerCase();
              const alias = (p: string, url?: string) => {
                const k = normalize(p);
                const u = normalize(url || '');
                if (k === 'x' || k.includes('x.com') || u.includes('x.com')) return 'twitter';
                if (k.includes('twitter') || u.includes('twitter.com')) return 'twitter';
                return k;
              };
              const ALLOWED = new Set(['facebook','instagram','tiktok','twitter','linkedin']);
              const raw = Object.entries((user as any)?.socialLinks || (user as any)?.social || {})
                .filter(([_, url]) => url && String(url).trim() !== '');
              const entries = raw
                .map(([p, u]) => [alias(String(p), String(u)), u] as [string, any])
                .filter(([p]) => ALLOWED.has(String(p)));
              entries.sort((a, b) => PRIORITY.indexOf(String(a[0])) - PRIORITY.indexOf(String(b[0])));
              const limit = Number(b?.limit ?? 3);
              if (!entries.length) return <div className="text-xs text-gray-400">No social links</div>;
              return (
                <div className="flex justify-start gap-3 flex-wrap">
                  {entries.slice(0, limit).map(([platform, url]) => (
                    <a key={String(platform)} href={String(url)} target="_blank" rel="noopener noreferrer"
                       onClick={() => { try { onAnalyticsUpdate?.(cardOwnerId, 'socialClicks'); } catch {} }}
                       className="p-2 rounded-full shadow-sm border hover:shadow transition"
                       style={{ background: colors.cardBg, borderColor: colors.border }}>
                      <UnifiedBrandIcon platform={String(platform)} kind="social" size={20} title={String(platform)} className="" color={ac} />
                    </a>
                  ))}
                </div>
              );
            })()}
            {b?.type==='streaming' && (() => {
              const allowed = new Set(['spotify','youtube','soundcloud','boomplay','deezer','tidal','audiomack']);
              const raw = Object.entries((user as any)?.streamingLinks || {})
                .filter(([platform, url]) => url && String(url).trim() !== '' && allowed.has(String(platform).toLowerCase()));
              if (!raw.length) return <div className="text-xs text-gray-400">No streaming links</div>;
              return (
                <div className="flex flex-wrap gap-2">
                  {raw.map(([platform, url]) => (
                    <a key={String(platform)} href={String(url)} target="_blank" rel="noopener noreferrer"
                       onClick={() => { try { onAnalyticsUpdate?.(cardOwnerId, 'socialClicks'); } catch {} }}
                       className="flex items-center gap-1 text-xs px-2 py-1 rounded border"
                       style={{ background: colors.cardBg, color: colors.text, borderColor: colors.border }}>
                      <UnifiedBrandIcon platform={String(platform)} kind="streaming" size={14} title={String(platform)} className="" color={ac} />
                      {String(platform)}
                    </a>
                  ))}
                </div>
              );
            })()}
          </div>
        ))}
      </div>

    {/* Contact Exchange Button */}
      {onContactExchange && !isOwnerView && (
        <div className="px-4 mt-4">
          <button
            onClick={onContactExchange}
            className="w-full flex items-center justify-center gap-2 rounded-xl font-semibold py-3 hover:opacity-90 transition-opacity"
            style={{ background: ac, color: '#fff' }}
          >
            <UserPlus className="w-4 h-4" />
            Send {user?.name?.split(' ')[0] || 'them'} your contact
          </button>
        </div>
      )}

    {/* Save + Ads */}
      <div className="px-4 mt-4">
        {blocks.filter((b:any)=>String(b?.type)==='save').map((b:any,i:number)=>(
      <button key={i} className="w-full rounded-xl font-semibold py-3"
          onClick={saveToContacts}
          style={{ background:(b?.bg||colors.saveBg), color:(b?.fg||colors.saveFg) }}>
            {b?.label||content.saveLabel||'SAVE CARD'}
          </button>
        ))}
      </div>
      <div className="px-4 my-4">
        {blocks.filter((b:any)=>String(b?.type)==='ads').map((b:any,i:number)=> (
          <div key={i}>
            {/* Always render carousel so height is reserved even before ads load */}
            <AdsCarousel ads={ads} accent={ac} cardHeight={176} />
          </div>
        ))}
      </div>
      {/* Modals for email lead form and QR */}
      <EnhancedEmailLeadForm
        isOpen={showEmail}
        onClose={() => setShowEmail(false)}
        recipientName={user?.name || content.title || 'Card Owner'}
        recipientEmail={user?.email || ''}
        cardOwnerId={cardOwnerId}
        onLeadSubmit={onLeadSubmit}
        onAnalyticsUpdate={() => onAnalyticsUpdate?.(cardOwnerId, 'formSubmissions')}
      />
      <QRCodeModal isOpen={showQR} onClose={() => setShowQR(false)} user={user} />
  {/* share popup handled by global ShareProvider */}
      </div>
    </div>
  );
};

export const DynamicTemplate: React.FC<Props> = ({ template, user, accent, mode = "grid", allowInnerScroll, ads, isOwnerView, leads = [], onLeadSubmit, onAnalyticsUpdate, onContactExchange }) => {
  const spec = (template.spec || {}) as DynBlocksSpec;
  // If blocks-schema present, render via BlocksSpecRenderer and short-circuit
  if (Array.isArray(spec.blocks) && spec.blocks.length) {
    // Compute a best-effort accent early for blocks path
    const uColors = ((user as any)?.colors || {}) as Record<string, string>;
    const computedAccent = accent || uColors.accent || uColors.primary || (spec?.theme?.accent as any) || (spec as any)?.accent || '#2563eb';
  return <BlocksSpecRenderer spec={spec} user={user} mode={mode} ads={ads} onAnalyticsUpdate={onAnalyticsUpdate} onLeadSubmit={onLeadSubmit} accent={computedAccent} isOwnerView={isOwnerView} onContactExchange={onContactExchange} />;
  }

  // Helpers to read loose dynamic-v1 fields
  const pick = <T,>(...vals: Array<T | undefined>) => vals.find(v => v !== undefined);

  // Basic primitives (extend as you grow)
  // Normalize URLs coming from server or local assets
  const normalizeUrl = (u?: string) => {
    if (!u) return undefined;
    const s = String(u).trim();
    if (!s) return undefined;
    // Rebase central-site upload URLs to same-origin relative paths on card subdomains
    if (/^https?:\/\//i.test(s) && s.includes('/uploads/')) {
      const idx = s.indexOf('/uploads/');
      return s.slice(idx);
    }
    if (/^(https?:\/\/|blob:|data:)/i.test(s)) return s;
    return resolveMediaUrl(s);
  };

  // Header: support aliases like hero/background
  const headerType = pick<string>(spec.header?.type, spec.hero?.type, spec.background?.type, "image");
  // Prefer user-provided cover first (customization), then explicit header/hero assets; finally fall back to template preview
  // Build raw header in priority order, but in owner panel we must not fall back to template artwork
  const rawHeaderSrc  = ((): string | undefined => {
    const userCover = (user as any)?.coverUrl || (user as any)?.coverImage;
    if (userCover) return userCover;
    // Always use system default when the user has not set a cover; do not fall back to template artwork
    return DEFAULT_COVER;
  })();
  const headerSrcCandidate = normalizeUrl(rawHeaderSrc);
  const [headerSrc, setHeaderSrc] = React.useState<string>(headerSrcCandidate || DEFAULT_COVER);
  React.useEffect(() => {
    setHeaderSrc(headerSrcCandidate || DEFAULT_COVER);
     
  }, [headerSrcCandidate]);
  const headerH    = pick<number>(spec.header?.height, spec.hero?.height, 160)!;
  // Soft Blossom/Bloom v1: give the cover more room in grid (Template Selection) for better visibility
  const isSoftBlossom = (() => {
    const key = String(template?.componentKey || '').toLowerCase();
    const nm  = String(template?.name || '').toLowerCase();
    return key.includes('soft-blossom') || key.includes('soft-bloom') || nm.includes('soft blossom') || nm.includes('soft bloom');
  })();
  const headerHeight = (mode === 'grid' && isSoftBlossom) ? (headerH + 20) : headerH;

  // Allow templates to hint the focal point; nudge Soft Blossom a bit lower to reduce top-crop in grid
  const baseObjectPosition = (spec?.header as any)?.objectPosition as (string | undefined);
  const objectPosition = (mode === 'grid' && isSoftBlossom)
    ? (baseObjectPosition || 'center 22%')
    : (baseObjectPosition || undefined);

  // Avatar: allow aliases logo/profile
  const avatarSpec = pick<any>(spec.avatar, spec.logo, spec.profile) || {};
  const avatar = {
    show: avatarSpec.show ?? true,
    shape: avatarSpec.shape || "rounded", // "circle" | "rounded" | "square"
    size: avatarSpec.size ?? 88,
    border: avatarSpec.border ?? true,
    fallback: avatarSpec.fallback,
  };
  // Avatar with default fallback when missing/broken
  const avatarCandidate = normalizeUrl(
    user?.avatarUrl || avatarSpec.src || template.assets?.avatar || template.assets?.logo || avatar.fallback
  );
  const [avatarSrc, setAvatarSrc] = React.useState<string | undefined>(
    avatarCandidate || avatarFallbackUrl(user?.displayName || user?.name || (user as any)?.username)
  );
  React.useEffect(() => {
    setAvatarSrc(avatarCandidate || avatarFallbackUrl(user?.displayName || user?.name || (user as any)?.username));
     
  }, [avatarCandidate]);

  // Theme/colors: accept theme.primary or colors.primary
  const layout = pick<string>(typeof spec.layout === 'string' ? (spec.layout as string) : undefined, "left"); // "left" | "center" | "right"
  const theme = pick<any>(spec.theme, spec.colors, spec.palette) || {};
  const userColors = ((user as any)?.colors || {}) as Record<string, string>;
  const palette = {
    primary: pick<string>(accent, userColors.accent, userColors.primary, theme.primary, theme.accent, "#2563eb")!,
    secondary: pick<string>(userColors.secondary, theme.secondary, "#1e40af")!,
    text: pick<string>(userColors.text, theme.text, "#111827")!,
    background: pick<string>(userColors.background, theme.background, "#ffffff")!,
    stripe: pick<string>(userColors.stripe || (userColors as any).stripLine, theme.stripe, "#3b82f6")!,
    saveButton: pick<string>(userColors.saveButton, theme.saveButton, undefined) || undefined,
    companyBadge: pick<string>(userColors.companyBadge, theme.companyBadge, undefined) || undefined,
  } as const;

  // Subscription gating (used only to warn/deny after ownership is confirmed)
  const nowTs = Date.now();
  const sub = (user as any)?.subscription || {};
  const notExpired = !sub?.expiresAt || (new Date(sub.expiresAt).getTime() > nowTs);
  const subActive = ((sub?.isActive ?? true) && notExpired);
  const isBroadcastOwnerEnabled = Boolean(isOwnerView && subActive && (sub?.type === 'gold' || sub?.type === 'platinum'));

  // Derive richer sections similar to the reference preview
  // Actions can be strings (well-known types) or objects; normalize to label+href
  const normalizeActions = (items?: any[]): Array<{ label: string; href?: string }> => {
    if (!Array.isArray(items)) return [];
    const out: Array<{ label: string; href?: string }> = [];
    for (const it of items) {
      if (!it) continue;
      if (typeof it === 'string') {
        const t = it.toLowerCase();
  if (t === 'call' || t === 'phone') out.push({ label: 'Call', href: contact.phone ? `tel:${contact.phone}` : undefined });
  else if (t === 'email' || t === 'mail') out.push({ label: 'Email', href: contact.email ? '#email' : undefined });
        else if (t === 'website' || t === 'link') out.push({ label: 'Website', href: contact.website ? String(contact.website) : undefined });
        else if (t === 'qr' || t === 'qrcode') out.push({ label: 'QR Code', href: '#qr' });
        else if (t === 'share') out.push({ label: 'Share', href: '#share' });
        else if (t === 'broadcast') out.push({ label: 'Broadcast', href: '#broadcast' });
        else out.push({ label: it });
      } else if (typeof it === 'object') {
        const type = String((it.type || it.kind || '')).toLowerCase();
        if (type) {
          if (type === 'call' || type === 'phone') out.push({ label: it.label || 'Call', href: contact.phone ? `tel:${contact.phone}` : it.href });
          else if (type === 'email' || type === 'mail') out.push({ label: it.label || 'Email', href: contact.email ? '#email' : it.href });
          else if (type === 'website' || type === 'link') out.push({ label: it.label || 'Website', href: it.href || (contact.website ? String(contact.website) : undefined) });
          else if (type === 'qr' || type === 'qrcode') out.push({ label: it.label || 'QR Code', href: it.href || '#qr' });
          else if (type === 'share') out.push({ label: it.label || 'Share', href: it.href || '#share' });
          else if (type === 'broadcast') out.push({ label: it.label || 'Broadcast', href: it.href || '#broadcast' });
          else out.push({ label: it.label || type || 'Action', href: it.href });
        } else {
          out.push({ label: it.label || 'Action', href: it.href });
        }
      }
    }
    return out;
  };
  // About: accept many aliases and preview-friendly fallbacks
  const aboutRaw = pick<any>(
    (typeof spec.about === 'object' ? spec.about?.text : undefined),
    (typeof spec.about === 'object' ? spec.about?.content : undefined),
    (spec.sections as any)?.about?.text,
    (spec.copy as any)?.about,
    (typeof spec.about === 'string' ? spec.about : undefined),
    spec.bio,
    user?.bio,
    user?.about,
    ""
  );
  const aboutText = typeof aboutRaw === 'string' ? aboutRaw : "";
  const isPreviewLike = (template?.id === 'preview') || (template as any)?.status === 'uploaded';
  const fallbackPreviewAbout = "Creative professional passionate about crafting intuitive digital experiences.";
  const effectiveAbout = aboutText || (isPreviewLike ? fallbackPreviewAbout : "");
  const contact = {
    phone: user?.phone || spec.contact?.phone || spec.phone,
    email: user?.email || spec.contact?.email || spec.email,
    website: user?.website || spec.contact?.website || spec.website,
    location: user?.location || spec.contact?.location || spec.location,
  };
  // Merge social + streaming links from user edits and spec, preferring user-provided values
  const normalizePlatformKey = (k: string) => {
    const key = String(k || "").toLowerCase();
    if (key === "x" || key.includes("twitter")) return "twitter";
    if (key.includes("linkedin")) return "linkedin";
    if (key.includes("instagram")) return "instagram";
    if (key.includes("facebook")) return "facebook";
    if (key.includes("tiktok")) return "tiktok";
    if (key.includes("youtube")) return "youtube";
    if (key.includes("spotify")) return "spotify";
    if (key.includes("applemusic") || key.includes("apple-music") || key === "apple") return "appleMusic";
    if (key.includes("soundcloud")) return "soundcloud";
    if (key.includes("deezer")) return "deezer";
    if (key.includes("twitch")) return "twitch";
    return key;
  };
  const cleanRecord = (obj: any) => {
    const out: Record<string, string> = {};
    if (!obj || typeof obj !== "object") return out;
    for (const [k, v] of Object.entries(obj)) {
      const s = String(v || "").trim();
      if (!s) continue;
      out[normalizePlatformKey(k)] = s;
    }
    return out;
  };
  const social = {
    ...cleanRecord(spec.social || {}),
    ...cleanRecord((user as any)?.social || (user as any)?.socialLinks || {}),
    ...cleanRecord((user as any)?.streamingLinks || {}),
  } as Record<string, string>;

  // Fallback actions if none defined
  let actions: Array<{ label: string; href?: string }> = normalizeActions(spec.actions);
  if (!actions.length) {
    const fallback: Array<{ label: string; href?: string }> = [];
  if (contact.phone) fallback.push({ label: "Call", href: `tel:${contact.phone}` });
  if (contact.email) fallback.push({ label: "Email", href: `#email` });
    if (contact.website) fallback.push({ label: "Website", href: String(contact.website) });
    // Add common utility actions to match standard previews
    fallback.push({ label: "QR Code", href: "#qr" });
    fallback.push({ label: "Share", href: "#share" });
    fallback.push({ label: "Broadcast", href: "#broadcast" });
    actions = fallback;
  }
  // New rule: keep Broadcast visible to everyone; click will prompt login + ownership check

  // scroll only in full preview dialogs for consistency, unless explicitly overridden
  const allowScroll = (typeof allowInnerScroll === 'boolean') ? allowInnerScroll : (mode !== "grid");

  // Scale-to-fit for inline (grid) previews to avoid showing scrollbars while keeping content visible
  const outerRef = React.useRef<HTMLDivElement | null>(null);
  const innerAllRef = React.useRef<HTMLDivElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = React.useState<number>(1);
  React.useLayoutEffect(() => {
    if (mode !== "grid") { setScale(1); return; }
  const el = outerRef.current;
  const content = contentRef.current;
    if (!el || !content) return;
    const compute = () => {
      const avail = el.clientHeight;
      const need = (innerAllRef.current ? innerAllRef.current.scrollHeight : content.scrollHeight);
      const s = need > 0 ? Math.min(1, avail / need) : 1;
      setScale(s);
    };
    compute();
  const RO: any = (window as any).ResizeObserver;
  const ro = RO ? new RO(compute) : null;
  if (ro) {
      try { ro.observe(el); } catch {}
      try { ro.observe(content); } catch {}
      if (innerAllRef.current) { try { ro.observe(innerAllRef.current); } catch {} }
    } else {
      window.addEventListener('resize', compute);
    }
    return () => {
      if (ro) {
        try { ro.disconnect(); } catch {}
      } else {
        window.removeEventListener('resize', compute);
      }
    };
  }, [mode, headerH]);
  // use shared AdsCarousel to match behavior of other templates

  // Local UI state for modals (email lead, QR, broadcast)
  const [showEmail, setShowEmail] = React.useState(false);
  const [showQR, setShowQR] = React.useState(false);
  const { openShare } = useShare();
  const [showBroadcast, setShowBroadcast] = React.useState(false);
  const [authedLeads, setAuthedLeads] = React.useState<any[] | null>(null);
  const [showLogin, setShowLogin] = React.useState(false);

  // Handlers for action buttons
  const handleAction = (label: string, href?: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const t = label.toLowerCase();
    if (t.includes('email')) { setShowEmail(true); return; }
    // Record call clicks for call/phone actions
    if (t.includes('call') || t.includes('phone') || (href && href.toLowerCase().startsWith('tel:'))) {
      try { onAnalyticsUpdate?.(String(user?.id || (user as any)?._id || (user as any)?.username || ''), 'callClicks'); } catch {}
    }
    if (t.includes('qr')) {
      setShowQR(true);
      // Emit analytics for QR modal opens (parity with static templates)
      try { onAnalyticsUpdate?.(String(user?.id || (user as any)?._id || (user as any)?.username || ''), 'qrScans'); } catch {}
      return;
    }
    if (t.includes('broadcast')) {
      // Only Gold/Platinum subscribers should see login for ownership verification
      const s = (user as any)?.subscription || {};
      const okTier = (s?.type === 'gold' || s?.type === 'platinum');
      const okActive = ((s?.isActive ?? true) && (!s?.expiresAt || new Date(s.expiresAt).getTime() > Date.now()));
      if (okTier && okActive) {
        setShowLogin(true);
        return;
      }
      // Non-eligible: show only the soft token message
      try { safeToast('info', 'This function only available for Gold or Platinum User, Subscribe Now !'); } catch {}
      return;
    }
    if (t.includes('share')) {
      const handle = (user as any)?.handle || (user as any)?.username || '';
      const username = (user as any)?.username || (user as any)?.id || '';
      const fallbackUrl = typeof window !== 'undefined' ? window.location.href : '';
      const url = (handle && username) ? canonicalCardUrl(handle, username) : fallbackUrl;
      try {
        const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS/i.test(navigator.userAgent||'');
        const payload = buildSharePayload({ name: (user as any)?.name || '', url });
        if ((navigator as any)?.share && isMobile) {
          (navigator as any).share(payload).catch(()=> openShare(url, payload.text));
        } else {
          openShare(url, payload.text);
        }
      } catch { openShare(url); }
      // Record share analytics best-effort
      try { onAnalyticsUpdate?.(String(user?.id || (user as any)?._id || (user as any)?.username || ''), 'shares'); } catch {}
      return;
    }
    if (href && href !== '#') { window.open(href, href.startsWith('http') ? '_blank' : '_self'); }
  };

  // Derive CTA/save to contacts color
  const saveBtnStyle: React.CSSProperties = { background: palette.saveButton || palette.primary, color: '#fff' };
  // v1 renderer: unify save action with vCard generation
  const saveToContactsV1 = React.useCallback(() => {
    try {
      const name = (user?.name || spec.fallbackName || template.name || '');
      const org = (user?.company || '');
      const title = (user?.position || spec.fallbackTitle || spec.subtitle || '');
      const phone = (user?.phone || spec.contact?.phone || spec.phone || '');
      const email = (user?.email || spec.contact?.email || spec.email || '');
      const website = (user?.website || spec.contact?.website || spec.website || '');
      const location = (user as any)?.location || spec.contact?.location || spec.location || '';
      const socials = { ...(spec.social || {}), ...(((user as any)?.social || (user as any)?.socialLinks) || {}) } as Record<string,string>;
      const streaming = { ...(((user as any)?.streamingLinks) || {}) } as Record<string,string>;
      const lines = [
        'BEGIN:VCARD','VERSION:3.0',
        `N:;${name};;;`,`FN:${name}`,
        org ? `ORG:${org}` : '',
        title ? `TITLE:${title}` : '',
        phone ? `TEL;TYPE=CELL:${phone}` : '',
        email ? `EMAIL:${email}` : '',
        website ? `URL:${website}` : '',
        location ? 'ADR;TYPE=WORK:;;;;;;' : '',
        location ? `LABEL;TYPE=WORK:${location}` : '',
        ...Object.entries(socials).map(([k, v]) => {
          const p = String(k).toLowerCase();
          const url = String(v).trim();
          if (!url) return '';
          if (['twitter','x','facebook','instagram','linkedin','tiktok','youtube'].includes(p)) {
            const type = p === 'x' ? 'twitter' : p;
            return `X-SOCIALPROFILE;type=${type}:${url}`;
          }
          return `URL:${url}`;
        }),
        ...Object.entries(streaming).map(([platform, url]) => (url ? `URL:${String(url)}` : '')),
        (() => {
          const socialsNote = Object.entries(socials).map(([k,v]) => `${k}: ${v}`).join(' | ');
          const streamingNote = Object.entries(streaming).map(([k,v]) => `${k}: ${v}`).join(' | ');
          const parts = [] as string[];
          if (socialsNote) parts.push(`Socials -> ${socialsNote}`);
          if (streamingNote) parts.push(`Streaming -> ${streamingNote}`);
          return parts.length ? `NOTE:${parts.join(' \n ')}` : '';
        })(),
        'END:VCARD'
      ].filter(Boolean);
      const vCardData = lines.join('\n');
      const blob = new Blob([vCardData], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${(name || 'contact').replace(/\s+/g, '_')}.vcf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      try { onAnalyticsUpdate?.(String(user?.id || user?._id || ''), 'saves'); } catch {}
    } catch {}
  }, [onAnalyticsUpdate, spec, template.name, user]);

  return (
    // Fit inside the bezel or container
  <div
      ref={outerRef}
      className={`w-full h-full overflow-x-hidden ${allowScroll ? "overflow-y-auto" : "overflow-y-hidden"} rounded-3xl overflow-hidden antialiased`}
      style={{
        background: palette.background,
        color: palette.text,
        // expose palette as CSS variables for any nested content
        ['--tc-primary' as any]: palette.primary,
        ['--tc-secondary' as any]: palette.secondary,
        ['--tc-text' as any]: palette.text,
        ['--tc-bg' as any]: palette.background,
        ['--tc-stripe' as any]: palette.stripe,
        ['--tc-save' as any]: palette.saveButton || palette.primary,
        ['--tc-company-badge' as any]: palette.companyBadge || palette.primary,
      }}
    >
  <div ref={innerAllRef} style={{ transform: mode === 'grid' ? `scale(${scale})` : undefined, transformOrigin: 'top center', marginTop: (mode === 'grid' ? (isSoftBlossom ? 0 : 8) : 0) }}>
      {/* Header */}
  <div className="w-full overflow-hidden" style={{ height: headerHeight }}>
        {headerType === "image" ? (
          <img
            src={headerSrc}
            alt=""
            className="w-full h-full object-cover"
            style={objectPosition ? ({ objectPosition } as React.CSSProperties) : undefined}
            onError={(e) => {
              const el = e.currentTarget as HTMLImageElement;
              // If not already the local default, try it first; then fall back to remote stock
              if (el.src !== DEFAULT_COVER) {
                setHeaderSrc(DEFAULT_COVER);
              } else {
                setHeaderSrc(REMOTE_FALLBACK_COVER_URL);
              }
            }}
          />
        ) : (
          <div
            className="w-full h-full"
            style={{
              background:
                headerType === "gradient"
                  ? (spec.header?.gradient ?? "linear-gradient(90deg,#009ffd,#2a2a72)")
                  : (spec.header?.color ?? "#0ea5e9"),
            }}
          />
        )}
  </div>
  {/* Optional decorative stripe bound to palette.stripe */}
  <div style={{ height: 3, background: palette.stripe }} />

  {/* Profile + actions */}
  <div ref={contentRef} className="p-4" style={{ textAlign: layout as any }}>
        {avatar.show && avatarSrc && (
          <img
            src={avatarSrc}
            alt=""
            onError={(e) => {
              const el = e.currentTarget as HTMLImageElement;
              const fb = avatarFallbackUrl(user?.displayName || user?.name || (user as any)?.username);
              if (el.src !== fb) {
                setAvatarSrc(fb);
              }
            }}
            style={{
              width: avatar.size,
              height: avatar.size,
              objectFit: "cover",
              borderRadius: avatar.shape === "circle" ? "9999px" : avatar.shape === "rounded" ? "16px" : "0px",
              border: avatar.border ? "3px solid white" : "none",
              marginTop: (mode === 'grid') ? (-avatar.size / 2 + 12) : (-avatar.size / 2),
            }}
            className="inline-block bg-white shadow"
          />
        )}
        <div className="mt-3">
     <div className="text-base font-semibold" style={{ color: palette.text }}>{user?.name || spec.fallbackName || template.name}</div>
     <div className="text-sm" style={{ color: `${palette.text}B3` }}>{user?.position || spec.fallbackTitle || spec.subtitle || ""}</div>
     <div className="text-sm" style={{ color: `${palette.text}B3` }}>{user?.company || spec.company || ""}</div>
          {/* Optional badge/tag under the title to match reference look */}
      { (((typeof spec.badge === 'object' ? spec.badge?.label : undefined) || spec.badge || spec.tag)) && (
            <div className="inline-flex mt-2 px-2 py-1 rounded-md text-xs font-medium"
       style={{ background: `${(palette.companyBadge || palette.primary)}1A`, color: (palette.companyBadge || palette.primary) }}>
        {typeof spec.badge === 'object' ? (spec.badge.label || 'Design') : (spec.badge || spec.tag)}
            </div>
          )}
        </div>

        {/* Action row */}
        {!!actions.length && (
      <div className="mt-3 grid grid-cols-3 gap-2">
            {actions.slice(0, 6).map((a, i) => (
              <a
                key={i}
                href={a.href || "#"}
                onClick={handleAction(a.label, a.href)}
        className="inline-flex h-9 items-center justify-center rounded-xl border bg-white text-sm"
        style={{ borderColor: palette.primary, color: palette.text }}
              >
                {a.label}
              </a>
            ))}
          </div>
        )}

        {/* About */}
    {effectiveAbout && (
          <div
            className="mt-4 rounded-xl p-4 text-sm border"
            style={{ background: `${palette.primary}14`, borderColor: `${palette.primary}33`, color: palette.text }}
          >
            <div className="font-medium mb-2" style={{ color: palette.primary }}>About</div>
            <div className="leading-relaxed">{effectiveAbout}</div>
          </div>
        )}

        {/* Contact & Social */}
        {(contact.phone || contact.email || contact.website || contact.location || Object.keys(social).length > 0) && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl border p-3 text-sm" style={{ borderColor: `${palette.primary}22`, color: palette.text }}>
              <div className="font-medium mb-2" style={{ color: palette.primary }}>Contact</div>
              {contact.phone && <div className="truncate">📞 <a
                style={{ color: palette.primary }}
                href={`tel:${contact.phone}`}
                onClick={() => { try { onAnalyticsUpdate?.(String(user?.id || (user as any)?._id || (user as any)?.username || ''), 'callClicks'); } catch {} }}
              >{contact.phone}</a></div>}
              {contact.email && <div className="truncate">✉️ <a style={{ color: palette.primary }} href="#email" onClick={(e)=>{ e.preventDefault(); setShowEmail(true); }}>{contact.email}</a></div>}
              {contact.website && <div className="truncate">🔗 <a style={{ color: palette.primary }} href={String(contact.website)} target="_blank" rel="noreferrer">{contact.website}</a></div>}
              {contact.location && <div className="truncate">📍 {contact.location}</div>}
            </div>
            <div className="rounded-xl border p-3 text-sm" style={{ borderColor: `${palette.primary}22`, color: palette.text }}>
              <div className="font-medium mb-2" style={{ color: palette.primary }}>Social</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(social).map(([k, v]: any, i) => (
                  v ? (
                    <a
                      key={i}
                      href={String(v)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-2 py-1 rounded border text-xs"
                      style={{ borderColor: palette.primary, color: palette.primary }}
                      onClick={() => { try { onAnalyticsUpdate?.(String(user?.id || (user as any)?._id || (user as any)?.username || ''), 'socialClicks'); } catch {} }}
                    >
                      {String(k)}
                    </a>
                  ) : null
                ))}
                {!Object.keys(social).length && <span className="text-gray-500 text-xs">No social links</span>}
              </div>
            </div>
        {/* share popup handled by global ShareProvider */}
          </div>
        )}

        {/* Contact Exchange Button */}
        {onContactExchange && !isOwnerView && (
          <div className="mt-4">
            <button
              onClick={onContactExchange}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl px-4 font-medium hover:opacity-90 transition-opacity"
              style={{ background: palette.primary, color: '#fff' }}
            >
              <UserPlus className="w-4 h-4" />
              Send {user?.name?.split(' ')[0] || 'them'} your contact
            </button>
          </div>
        )}

        {/* Save to Contacts */}
        <div className="mt-4">
          <button onClick={saveToContactsV1} className="inline-flex h-10 w-full items-center justify-center rounded-xl px-4 font-medium" style={saveBtnStyle}>
            {spec.saveContact?.label || "Save to Contacts"}
          </button>
        </div>
      </div>
  {/* Ads footer: hidden in grid for Soft Blossom v1 to preserve width and show Save clearly */}
  { !(mode === 'grid' && isSoftBlossom) && (
    <AdsCarousel ads={ads} accent={palette.primary} cardHeight={176} />
  ) }
      </div>
  {/* Modals: email lead form, QR, broadcast */}
  <EnhancedEmailLeadForm
    isOpen={showEmail}
    onClose={() => setShowEmail(false)}
    recipientName={user?.name || spec.fallbackName || template.name}
    recipientEmail={user?.email || contact.email || ''}
    cardOwnerId={String(user?.id || user?._id || user?.username || '')}
    onLeadSubmit={onLeadSubmit}
    onAnalyticsUpdate={() => onAnalyticsUpdate?.(String(user?.id || user?._id || ''), 'email_form')}
  />
  <QRCodeModal isOpen={showQR} onClose={() => setShowQR(false)} user={user} />
  <BroadcastModal isOpen={showBroadcast} onClose={() => setShowBroadcast(false)} userName={user?.name || ''} cardOwnerId={String(user?.id || user?._id || '')} leads={(authedLeads ?? leads) as any} />
  <BroadcastLoginModal
    isOpen={showLogin}
    onClose={() => setShowLogin(false)}
    onSuccess={async ({ id }) => {
      // Verify the authenticated owner matches this card
      const currentId = String(user?.id || (user as any)?._id || '');
      if (!currentId) { safeToast('error', 'Missing card owner id'); return; }
      if (String(id) !== currentId) {
        safeToast('error', 'This account does not own this card. Access denied.');
        return;
      }
      // Check subscription status before opening
      const now = Date.now();
      const s = (user as any)?.subscription || {};
      const ok = ((s?.isActive ?? true) && (!s?.expiresAt || new Date(s.expiresAt).getTime() > now) && (s?.type === 'gold' || s?.type === 'platinum'));
      if (!ok) {
        safeToast('warning', 'Broadcast requires an active Gold or Platinum subscription.');
        return;
      }
      // Fetch fresh leads for this owner (requires auth)
      try {
        const fresh = await listLeadsByOwner(String(currentId)).catch(() => null);
        if (fresh && Array.isArray(fresh)) setAuthedLeads(fresh as any);
      } catch {}
      setShowBroadcast(true);
      try { onAnalyticsUpdate?.(String(currentId), 'messageClicks'); } catch {}
    }}
  />
    </div>
  );
};
